// Google Analytics y SEO Scripts para A la Vuelta 360

// ID de medición (centralizado)
const GA_MEASUREMENT_ID = "G-F6NMDK6CWE";

// Configuración de Google Analytics 4
export const initializeGA = (measurementId = GA_MEASUREMENT_ID) => {
  // Evitar inicializar múltiples veces
  if (window.gaInitialized) return;
  window.gaInitialized = true;

  // Cargar Google Analytics con manejo de error
  const script1 = document.createElement("script");
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script1.onerror = function () {
    console.error(
      "[GA] Error: No se pudo cargar el script de Google Analytics. Verifica tu conexión, adblockers o el ID de medición.",
    );
  };
  script1.onload = function () {
    console.info("[GA] Script de Google Analytics cargado correctamente.");
  };
  document.head.appendChild(script1);

  // Configurar Google Analytics
  const script2 = document.createElement("script");
  script2.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${measurementId}', {
      page_title: document.title,
      page_location: window.location.href,
      cookie_domain: 'alavuelta360.com.mx',
      cookie_flags: 'SameSite=None;Secure',
      anonymize_ip: true,
      send_page_view: false,
      custom_map: {
        'custom_parameter_1': 'evento_tipo',
        'custom_parameter_2': 'producto_interes'
      }
    });
  `;
  document.head.appendChild(script2);
};

// Tracking de vistas de página (para SPA con React Router)
export const trackPageView = (path, title) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.origin + path,
    });
    console.info(`[GA] PageView: ${path}`);
  }
};

// Función para tracking de eventos personalizados
export const trackEvent = (eventName, parameters = {}) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    try {
      window.gtag("event", eventName, {
        event_category: "engagement",
        event_label: parameters.label || "",
        value: parameters.value || 0,
        ...parameters,
      });
    } catch (err) {
      console.warn("gtag call failed", err);
    }
  }
};

// Tracking de eventos específicos del negocio
export const trackProductView = (productName, productId) => {
  trackEvent("view_item", {
    event_category: "ecommerce",
    event_label: productName,
    item_id: productId,
    item_name: productName,
    item_category: "equipment_rental",
    currency: "MXN",
  });
  console.info(`[GA] ProductView: ${productName} (ID: ${productId})`);
};

export const trackQuoteRequest = (productName, packageName) => {
  trackEvent("generate_lead", {
    event_category: "conversion",
    event_label: `${productName} - ${packageName}`,
    currency: "MXN",
    value: 1,
  });
  console.info(`[GA] QuoteRequest: ${productName} - ${packageName}`);
};

export const trackContactForm = (formType) => {
  trackEvent("form_submit", {
    event_category: "engagement",
    event_label: formType,
    value: 1,
  });
  console.info(`[GA] ContactForm: ${formType}`);
};

// Eventos adicionales para el negocio
export const trackWhatsAppClick = (source, productName = "") => {
  trackEvent("whatsapp_click", {
    event_category: "engagement",
    event_label: productName || source,
    source: source,
  });
  console.info(`[GA] WhatsApp Click: ${source}`);
};

export const trackVideoPlay = (videoName, source) => {
  trackEvent("video_play", {
    event_category: "engagement",
    event_label: videoName,
    source: source,
  });
};

export const trackReelInteraction = (action, reelId) => {
  trackEvent("reel_interaction", {
    event_category: "engagement",
    event_label: action,
    reel_id: reelId,
  });
};

export const trackChatbotInteraction = (action) => {
  trackEvent("chatbot_interaction", {
    event_category: "engagement",
    event_label: action,
  });
};

export const trackCalendarView = (month, year) => {
  trackEvent("calendar_view", {
    event_category: "engagement",
    event_label: `${month}/${year}`,
  });
};

// ==========================================
// TRACKING DE CLICS - Análisis de comportamiento
// ==========================================

// Tracking de clics en botones CTA (Call to Action)
export const trackButtonClick = (buttonName, location, extraData = {}) => {
  trackEvent("button_click", {
    event_category: "click",
    event_label: buttonName,
    button_location: location,
    ...extraData,
  });
};

// Tracking de navegación (menú, links internos)
export const trackNavigation = (destination, source = "menu") => {
  trackEvent("navigation_click", {
    event_category: "navigation",
    event_label: destination,
    source: source,
  });
};

// Tracking de clics en productos (cards, imágenes, etc.)
export const trackProductClick = (productName, productId, clickLocation) => {
  trackEvent("product_click", {
    event_category: "product_interaction",
    event_label: productName,
    item_id: productId,
    click_location: clickLocation,
  });
};

// Tracking de clics en redes sociales
export const trackSocialClick = (platform, location) => {
  trackEvent("social_click", {
    event_category: "social",
    event_label: platform,
    click_location: location,
  });
};

// Tracking de scroll (para ver qué secciones ven los usuarios)
export const trackSectionView = (sectionName) => {
  trackEvent("section_view", {
    event_category: "engagement",
    event_label: sectionName,
  });
};

// Tracking de clics en galería de imágenes
export const trackGalleryClick = (imageName, productName) => {
  trackEvent("gallery_click", {
    event_category: "engagement",
    event_label: imageName,
    product: productName,
  });
};

// Tracking de descarga o compartir
export const trackShare = (contentType, method) => {
  trackEvent("share", {
    event_category: "engagement",
    event_label: contentType,
    method: method,
  });
};

// Schema.org para productos específicos
export const generateProductSchema = (product) => {
  return {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product.name,
    description: product.description,
    image: `https://www.alavuelta360.com.mx${product.image}`,
    brand: {
      "@type": "Brand",
      name: "A la Vuelta 360",
    },
    offers: {
      "@type": "Offer",
      url: `https://www.alavuelta360.com.mx/producto-personalizado/${product.id}`,
      priceCurrency: "MXN",
      price: product.price.replace(/[^0-9]/g, ""),
      availability: "https://schema.org/InStock",
      seller: {
        "@type": "Organization",
        name: "A la Vuelta 360",
      },
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "150",
    },
  };
};

// Inyectar Schema.org dinámicamente
export const injectProductSchema = (product) => {
  const existingScript = document.getElementById("product-schema");
  if (existingScript) {
    existingScript.remove();
  }

  const script = document.createElement("script");
  script.id = "product-schema";
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(generateProductSchema(product));
  document.head.appendChild(script);
};

// Configuración para Facebook Pixel (opcional)
export const initializeFacebookPixel = (pixelId) => {
  const script = document.createElement("script");
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(script);
};
